import { RequestQuery } from '@/data/types/RequestQuery';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { transactionsClubdp } from 'integration/generated/transactions';
import { useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import useSWR from 'swr';
import { useCallback } from 'react';
import { useNotifications } from '@/data/Content/Notifications';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { processError } from '@/data/utils/processError';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';

const clubdpCardsFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsClubdp(pub).clubdpCards(storeId, query, params);
		return res;
	};

const clubdpFlagsFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsClubdp(pub).clubdpFlags(storeId, query, params);
		return res;
	};

const clubdpCardUnlinkFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			password: string;
			email: string;
			cardType: number;
		},
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsClubdp(pub).clubdpCardUnlink(storeId, query, data, params);
		return res;
	};

const clubdpCardValidateFetcher =
	(pub: boolean) =>
	async (storeId: string, cardId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsClubdp(pub).clubdpCardValidate(storeId, cardId, query, params);
		return res;
	};

const clubdpCardForCheckOutFetcher =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams) => {
		const res = await transactionsClubdp(pub).clubdpCardForCheckOut(storeId, query, params);
		return res;
	};

const clubdpLoyaltyOtpDistributorFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			phone: string;
			cardId: string;
			email: string;
			password: string;
		},
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsClubdp(pub).clubdpLoyaltyOtpDistributor(
			storeId,
			data,
			query,
			params
		);
		return res;
	};

const clubdpLoyaltyCardLinkFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			phone: string;
			cardId: string;
			email: string;
			password: string;
			code: string;
		},
		query: RequestQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsClubdp(pub).clubdpLoyaltyCardLink(storeId, data, query, params);
		return res;
	};

const DATA_KEY_CLUBDP_CARDS = 'DATA_KEY_CLUBDP_CARDS';
const DATA_KEY_CLUBDP_FLAGS = 'DATA_KEY_CLUBDP_FLAGS';

export const useClubDpCards = (args?: {
	keepPreviousData?: boolean;
	revalidateOnMount?: boolean;
}) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();

	const {
		data,
		error,
		isLoading,
		mutate: mutateClubDpCards,
	} = useSWR(
		storeId ? [{ storeId }, DATA_KEY_CLUBDP_CARDS] : null,
		async ([{ storeId }]) => clubdpCardsFetcher(true)(storeId, {}, params),
		{
			keepPreviousData: args?.keepPreviousData ?? true,
			revalidateOnMount: args?.revalidateOnMount ?? true,
		}
	);

	const cardUnlink = useCallback(
		async (data: { password: string; email: string; cardType: number }) => {
			try {
				const response = await clubdpCardUnlinkFetcher(true)(storeId, data, {}, params);
				if (response.unlinked) {
					mutateClubDpCards();
				}
				return response;
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
				return null;
			}
		},
		[params, storeId, mutateClubDpCards, notifyError]
	);

	return {
		rawData: data,
		cards: data?.cards,
		error,
		loading: isLoading,
		mutateClubDpCards,
		cardUnlink,
	};
};

export const useClubDpFlags = (args?: {
	keepPreviousData?: boolean;
	revalidateOnMount?: boolean;
}) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const { data, error, isLoading, mutate } = useSWR(
		storeId ? [{ storeId }, DATA_KEY_CLUBDP_FLAGS] : null,
		async ([{ storeId }]) => clubdpFlagsFetcher(true)(storeId, {}, params),
		{
			keepPreviousData: args?.keepPreviousData ?? true,
			revalidateOnMount: args?.revalidateOnMount ?? true,
		}
	);
	if (data?.linked) {
		data.linked.rewards = false;
	}
	return {
		flags: data,
		error,
		loading: isLoading,
		mutateClubDpFlags: mutate,
	};
};

export const useClubDpCardValidate = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();

	const validateCard = useCallback(
		async (cardId: string) => {
			try {
				const response = await clubdpCardValidateFetcher(true)(storeId, cardId, {}, params);
				return response;
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
				return null;
			}
		},
		[params, storeId, notifyError]
	);

	return {
		validateCard,
	};
};

export const useClubDpCardForCheckOut = (options?: {
	revalidateOnMount?: boolean;
	keepPreviousData?: boolean;
}) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const { revalidateOnMount = true, keepPreviousData = true } = options || {};

	const { data, error, isLoading, mutate } = useSWR(
		storeId ? [{ storeId }, DATA_KEY_CLUBDP_FLAGS] : null,
		async ([{ storeId }]) => clubdpCardForCheckOutFetcher(true)(storeId, {}, params),
		{ keepPreviousData, revalidateOnMount }
	);

	return {
		card: data?.card,
		rawData: data,
		error,
		loading: isLoading,
		mutateClubDpCardForCheckOut: mutate,
	};
};

export const useClubDpLoyaltyDistributor = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();
	const loading = useIsOpen();
	const openOtp = useIsOpen();
	const openBlocked = useIsOpen();
	const openUpdateCard = useIsOpen();

	const { mutateClubDpCards } = useClubDpCards({
		keepPreviousData: false,
		revalidateOnMount: false,
	});
	const { mutateClubDpFlags } = useClubDpFlags({
		keepPreviousData: false,
		revalidateOnMount: false,
	});

	const loyaltyOtpDistributor = useCallback(
		async (data: { phone: string; cardId: string; email: string; password: string }) => {
			loading.on();
			try {
				const response = await clubdpLoyaltyOtpDistributorFetcher(true)(storeId, data, {}, params);

				if (response.sent) {
					openOtp.on();
					return;
				}

				throw new Error('No se pudo enviar el OTP');
			} catch (error) {
				const errorKey = getValueByPathFromUnknown(error, ['error', 'errors', 0, 'errorKey']);

				const errorMessage = getValueByPathFromUnknown(error, [
					'error',
					'errors',
					0,
					'errorMessage',
				]);

				switch (errorKey) {
					case 'ERR_LOYALTY_CARD_IN_USE':
						notifyError('Esta tarjeta ya está vinculada a una cuenta.', 'Error en vinculación');
						break;
					case 'ERR_LOYALTY_CARD_NOT_FOUND':
						notifyError(
							'La tarjeta no fue encontrada. Por favor, verifica los datos ingresados.',
							'Tarjeta no encontrada'
						);
						break;
					case 'ERR_LOYALTY_ONLY_REDEEM_CARD':
						openUpdateCard.on();
						break;
					case 'ERR_LOYALTY_USER_BLOCKED':
						openBlocked.on();
						break;
					case 'ERR_LOYALTY_CARD_MISMATCH_PHONE':
						notifyError(
							valueIsString(errorMessage)
								? errorMessage
								: 'El teléfono no corresponde a la tarjeta ingresada.',
							'Teléfono no coincide'
						);
						break;
					case 'ERR_LOYALTY_WRONG_CREDENTIALS':
						notifyError('Correo o contraseña incorrectos.', 'Error en credenciales');
						break;
					default:
						notifyError(processError(error as TransactionErrorResponse));
						break;
				}
				return null;
			} finally {
				loading.off();
			}
		},
		[notifyError, params, storeId, loading, openOtp, openUpdateCard, openBlocked]
	);

	const loyaltyCardLink = useCallback(
		async (data: {
			phone: string;
			cardId: string;
			email: string;
			password: string;
			code: string;
		}) => {
			loading.on();
			try {
				await clubdpLoyaltyCardLinkFetcher(true)(storeId, data, {}, params);
				await mutateClubDpCards();
				await mutateClubDpFlags();
			} catch (error) {
				notifyError(processError(error as TransactionErrorResponse));
			} finally {
				loading.off();
			}
		},
		[notifyError, params, storeId, loading, mutateClubDpCards, mutateClubDpFlags]
	);

	return {
		loyaltyOtpDistributor,
		loyaltyCardLink,
		loading: loading.open,
		openOtp: openOtp.open,
		openUpdateCard: openUpdateCard.open,
		openBlocked: openBlocked.open,
	};
};
